import { useMemo } from 'react'

/**
 * useMessageFollowUp
 *
 * Determines whether a follow-up CTA should be displayed for the current chat thread.
 *
 * A follow-up is warranted ONLY when:
 *   - The LAST message in the conversation was SENT BY ME (to_user_id === otherUserChatId)
 *   - No reply has been received from the recipient since then
 *   - 7 or more days have elapsed since that message was sent
 *
 * @param {Array}  messages         - The chat messages array from the store
 * @param {number} currentUserId    - The currently authenticated user's ID
 * @param {number} otherUserChatId  - The recipient/contact's chat user ID
 * @returns {{ showFollowUp: boolean, daysSince: number, isLongWait: boolean, lastMessageDate: Date|null }}
 */
export const formatMonthYear = (date) => {
    if (!date) return ''
    const d = date instanceof Date ? date : new Date(date)
    if (isNaN(d.getTime())) return ''
    return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
}

const useMessageFollowUp = (messages, currentUserId, otherUserChatId) => {
    return useMemo(() => {
        if (!messages || messages.length === 0 || !otherUserChatId) {
            return { showFollowUp: false, daysSince: 0, isLongWait: false, lastMessageDate: null, monthYear: '' }
        }

        // Get the last message in the thread
        const lastMessage = messages[messages.length - 1]

        // A message sent BY ME to the contact has to_user_id equal to the recipient (otherUserChatId)
        // or from_user_id NOT equal to the recipient.
        const lastMsgTo = parseInt(lastMessage?.to_user_id)
        const lastMsgFrom = parseInt(lastMessage?.from_user_id)
        const otherId = parseInt(otherUserChatId)

        const sentByMe = lastMsgTo === otherId || (lastMsgFrom !== otherId && !isNaN(lastMsgFrom))

        const noReply = lastMessage?.is_replied === 0 || lastMessage?.is_replied === '0' || lastMessage?.is_replied === undefined

        // If the last message was sent BY THE BRAND (not by me), OR has been replied to -> NO follow-up prompt!
        if (!sentByMe || !noReply) {
            return { showFollowUp: false, daysSince: 0, isLongWait: false, lastMessageDate: null, monthYear: '' }
        }

        const rawDate = lastMessage.created_at || lastMessage.timezone
        if (!rawDate) {
            return { showFollowUp: false, daysSince: 0, isLongWait: false, lastMessageDate: null, monthYear: '' }
        }

        const sentAt = new Date(rawDate)
        if (isNaN(sentAt.getTime())) {
            return { showFollowUp: false, daysSince: 0, isLongWait: false, lastMessageDate: null, monthYear: '' }
        }

        const now = new Date()
        const diffMs = now - sentAt
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

        if (diffDays >= 7) {
            return {
                showFollowUp: true,
                daysSince: diffDays,
                isLongWait: diffDays >= 30,
                lastMessageDate: sentAt,
                monthYear: formatMonthYear(sentAt)
            }
        }

        return { showFollowUp: false, daysSince: diffDays, isLongWait: false, lastMessageDate: null, monthYear: '' }
    }, [messages, currentUserId, otherUserChatId])
}

export default useMessageFollowUp
